/**
 * Chaves de funcionalidade e ações da matriz de acesso.
 *
 * Espelham o catálogo em `features` no banco (seed em
 * `1787000100000-SeedFeatureCatalog.ts`). Uma chave só existe aqui se existir
 * lá — o backend valida isso em `handler-coverage.spec.ts`.
 */

export type PermissionAction = 'view' | 'create' | 'edit' | 'delete';

export const FEATURES = {
  dashboardPrincipal: 'dashboard.principal',
  dashboardFinanceiro: 'dashboard.financeiro',

  notasFiscais: 'notas-fiscais.notas',
  /**
   * Acesso à *tela* de notas fiscais. Separado de `notasFiscais` porque a API
   * libera 9 perfis e a navegação só 3 — usar a chave da API no menu passaria
   * a exibir a tela para auditoria, TI, compras, RH, gestão e governança.
   */
  notasFiscaisTela: 'notas-fiscais.tela',
  /** Operação do financeiro: liberar, analisar e faturar NFs de todos. */
  notasFiscaisLiberacao: 'notas-fiscais.liberacao',

  feedbacksAvaliacoes: 'feedbacks.avaliacoes',
  feedbacksAvaliar: 'feedbacks.avaliar',
  feedbacksMeus: 'feedbacks.meus',
  feedbacksRh: 'feedbacks.rh',
  feedbacksPeriodos: 'feedbacks.periodos',

  usuarios: 'usuarios.usuarios',
  usuariosDocumentos: 'usuarios.documentos',
  usuariosBusca: 'usuarios.busca',
  usuariosDevolucao: 'usuarios.devolucao',
  meuGestor: 'usuarios.meu-gestor',
  meusColaboradores: 'usuarios.meus-colaboradores',
  meusDocumentos: 'usuarios.meus-documentos',
  /** Histórico de salário do colaborador (dado sensível). */
  pessoasSalarios: 'pessoas.salarios',
  /** Cadastro mestre: pessoa e vínculo (US-GP-001). */
  pessoasColaboradores: 'pessoas.colaboradores',
  /** CPF completo do colaborador; cada leitura é auditada. */
  pessoasDadosSensiveis: 'pessoas.dados-sensiveis',
  pessoasDocumentos: 'pessoas.documentos',
  pessoasDocumentosValidacao: 'pessoas.documentos-validacao',
  pessoasDocumentosExportacao: 'pessoas.documentos-exportacao',
  pessoasTiposDocumentais: 'pessoas.tipos-documentais',
  pessoasEnderecosContatos: 'pessoas.enderecos-contatos',
  pessoasAlteracoesCadastrais: 'pessoas.alteracoes-cadastrais',
  pessoasDadosBancarios: 'pessoas.dados-bancarios',
  pessoasDadosBancariosValidacao: 'pessoas.dados-bancarios-validacao',
  pessoasLotacoes: 'pessoas.lotacoes',
  pessoasMovimentacoes: 'pessoas.movimentacoes',
  pessoasMovimentacoesAprovacao: 'pessoas.movimentacoes-aprovacao',
  pessoasSituacaoFuncional: 'pessoas.situacao-funcional',
  pessoasAfastamentos: 'pessoas.afastamentos',
  pessoasTiposAfastamento: 'pessoas.tipos-afastamento',
  pessoasAfastamentosAprovacao: 'pessoas.afastamentos-aprovacao',
  pessoasAfastamentosMedico: 'pessoas.afastamentos-medico',
  pessoasDesligamentos: 'pessoas.desligamentos',
  pessoasDesligamentoModelos: 'pessoas.desligamento-modelos',
  pessoasDesligamentoTarefas: 'pessoas.desligamento-tarefas',
  pessoasReintegracoes: 'pessoas.reintegracoes',
  pessoasReintegracaoAprovacao: 'pessoas.reintegracao-aprovacao',
  pessoasReintegracaoJuridico: 'pessoas.reintegracao-juridico',
  pessoasFerias: 'pessoas.ferias',
  pessoasFeriasAprovacao: 'pessoas.ferias-aprovacao',
  pessoasFeriasPoliticas: 'pessoas.ferias-politicas',
  pessoasAlocacoes: 'pessoas.alocacoes',
  pessoasFeriasAcessos: 'pessoas.ferias-acessos',

  empresas: 'empresas.empresas',
  empresasContasBancarias: 'empresas.contas-bancarias',

  ativos: 'ativos.ativos',
  ativosDashboard: 'ativos.dashboard',
  ativosDocumentos: 'ativos.documentos',
  ativosResponsabilidade: 'ativos.responsabilidade',
  ativosManutencao: 'ativos.manutencao',
  ativosBaixa: 'ativos.baixa',
  ativosLicencas: 'ativos.licencas',
  ativosAlertas: 'ativos.alertas',
  ativosInventario: 'ativos.inventario',
  ativosCustodiarios: 'ativos.custodiarios',

  comprasRequisicoes: 'compras.requisicoes',
  comprasCotacoes: 'compras.cotacoes',
  comprasEqualizacoes: 'compras.equalizacoes',
  comprasGovernanca: 'compras.governanca',
  comprasAprovacaoGestor: 'compras.aprovacao-gestor',
  comprasAprovacaoFinanceiro: 'compras.aprovacao-financeiro',
  comprasAprovacaoGovernanca: 'compras.aprovacao-governanca',
  comprasAprovacaoDiretoria: 'compras.aprovacao-diretoria',
  comprasPagamentos: 'compras.pagamentos',
  comprasRelatorios: 'compras.relatorios',

  fornecedores: 'fornecedores.fornecedores',
  fornecedoresAvaliacoes: 'fornecedores.avaliacoes',
  fornecedoresContratos: 'fornecedores.contratos',


  clientes: 'clientes.clientes',
  clientesEnderecos: 'clientes.enderecos',
  clientesContatos: 'clientes.contatos',
  clientesDocumentos: 'clientes.documentos',
  clientesResponsaveis: 'clientes.responsaveis',
  clientesClassificacoes: 'clientes.classificacoes',
  clientesIndicadores: 'clientes.indicadores',
  clientesFusao: 'clientes.fusao',
  clientesAprovacoes: 'clientes.aprovacoes',
  clientesResponsabilidadesContato: 'clientes.responsabilidades-contato',
  clientesDocumentoFiscal: 'clientes.documento-fiscal',
  clientesAcessosExternos: 'clientes.acessos-externos',

  // Gestão Comercial — Oportunidades (US-COM-004). 11 features distintas
  // porque a spec §Perfis e Permissões lista 17 permissões antigas que
  // agrupamos por semântica (ver design.md §Perfis e Permissões e a
  // migration `1789000000000-SeedOportunidadesFeatures`).
  contratos: 'contratos.contratos',
  contratosAprovacao: 'contratos.aprovacao',
  contratosAssinatura: 'contratos.assinatura',
  contratosAditivos: 'contratos.aditivos',
  contratosModelos: 'contratos.modelos',
  contratosClausulas: 'contratos.clausulas',

  oportunidades: 'oportunidades.oportunidades',
  oportunidadesConfidencial: 'oportunidades.confidencial',
  oportunidadesDesfechos: 'oportunidades.desfechos',
  oportunidadesAprovacaoMargem: 'oportunidades.aprovacao-margem',
  oportunidadesProbabilidadeOverride: 'oportunidades.probabilidade-override',
  oportunidadesTransferencia: 'oportunidades.transferencia',
  oportunidadesGeracaoProposta: 'oportunidades.geracao-proposta',
  oportunidadesEtapas: 'oportunidades.etapas',
  oportunidadesMotivosPerda: 'oportunidades.motivos-perda',
  oportunidadesCriacaoSuspenso: 'oportunidades.criacao-suspenso',
  oportunidadesCriacaoRestrito: 'oportunidades.criacao-restrito',

  // Gestão Comercial — Base Comercial (US-COM-005). 9 features vindas da
  // migration `1789600000000-SeedBaseComercialFeatures`. Ver design.md
  // §D13 para o mapping de perfis (Executivo → `comercial`, Gerente →
  // `gestor_interno`, Financeiro → `finance`), aplicado por
  // `/admin/perfis` — a migration só concede a admin/governance/audit.
  baseComercial: 'base-comercial.bases',
  baseComercialAprovacao: 'base-comercial.aprovacao',
  baseComercialPublicacao: 'base-comercial.publicacao',
  baseComercialCancelamento: 'base-comercial.cancelamento',
  baseComercialDescontoAlcada: 'base-comercial.desconto-alcada',
  baseComercialUsoExpirada: 'base-comercial.uso-expirada',
  baseComercialReajuste: 'base-comercial.reajuste',
  baseComercialDominios: 'base-comercial.dominios',
  baseComercialFinanceiro: 'base-comercial.financeiro',

  // Gestão Comercial — Propostas Comerciais (US-COM-006). 15 features
  // vindas da migration `1789750000000-SeedProposalsFeatures`. Ver
  // design.md §D6/D9 para mapping de perfis (Executivo → `comercial`,
  // Analista Pré-Vendas → `pre_vendas`, Gerente → `gestor_interno`,
  // Diretoria → `gestor_interno` com feature dedicada, Financeiro →
  // `finance`, Jurídico → `juridico`); migration só concede a
  // admin/governance/audit.
  proposta: 'proposal.propostas',
  propostaCriacaoSuspenso: 'proposal.criacao-suspenso',
  propostaCriacaoRestrito: 'proposal.criacao-restrito',
  propostaSolicitarAprovacao: 'proposal.solicitar-aprovacao',
  propostaAprovar: 'proposal.aprovar',
  propostaAprovarFinanceiro: 'proposal.aprovar-financeiro',
  propostaAprovarJuridico: 'proposal.aprovar-juridico',
  propostaAprovarDescontoAcimaLimite: 'proposal.aprovar-desconto-acima-limite',
  propostaAprovarMargemAbaixoMin: 'proposal.aprovar-margem-abaixo-min',
  propostaOverrideBaseComercial: 'proposal.override-base-comercial',
  propostaEnviar: 'proposal.enviar',
  propostaRegistrarNegociacao: 'proposal.registrar-negociacao',
  propostaRegistrarAceite: 'proposal.registrar-aceite',
  propostaRegistrarRejeicao: 'proposal.registrar-rejeicao',
  propostaConverterEmContrato: 'proposal.converter-em-contrato',
  propostaCancelar: 'proposal.cancelar',
  propostaAssinaturaEletronica: 'proposal.assinatura-eletronica',
  propostaAdminWorkflows: 'proposal.admin-workflows',

  // Recrutamento e Seleção (change `modulo-recrutamento-selecao`). As 5
  // features de aprovação existem uma por passo da cadeia porque quem
  // aprova por RH/Financeiro/Diretoria é decisão de negócio tomada em
  // `/admin/perfis`, não papel fixo no código.
  recrutamentoRequisicoes: 'recrutamento.requisicoes',
  recrutamentoAprovacaoGestor: 'recrutamento.aprovacao-gestor',
  recrutamentoAprovacaoRh: 'recrutamento.aprovacao-rh',
  recrutamentoAprovacaoFinanceiro: 'recrutamento.aprovacao-financeiro',
  recrutamentoAprovacaoDiretoria: 'recrutamento.aprovacao-diretoria',
  recrutamentoAprovacaoRecrutamento: 'recrutamento.aprovacao-recrutamento',
  recrutamentoPublicacao: 'recrutamento.publicacao',
  recrutamentoCandidatos: 'recrutamento.candidatos',
  recrutamentoDadosSensiveis: 'recrutamento.dados-sensiveis',
  recrutamentoProcessoSeletivo: 'recrutamento.processo-seletivo',
  recrutamentoRetornoEtapa: 'recrutamento.retorno-etapa',

  cauCatalogo: 'cau.catalogo',
  cauChamados: 'cau.chamados',
  cauAtendimento: 'cau.atendimento',
  cauConfiguracoes: 'cau.configuracoes',
  cauAuditoria: 'cau.auditoria',
  cauAprovacoes: 'cau.aprovacoes',
  cauAcessos: 'cau.acessos',
  cauAtivos: 'cau.ativos',
  cauDashboard: 'cau.dashboard',
  cauRelatorios: 'cau.relatorios',

  masterDataCentrosDeCusto: 'master-data.centros-de-custo',
  masterDataProjetos: 'master-data.projetos',
  masterDataLocalidades: 'master-data.localidades',
  masterDataAreas: 'master-data.areas',
  masterDataCargos: 'master-data.cargos',
  masterDataFeriados: 'master-data.feriados',

  portalFornecedor: 'portal.fornecedor',
  portalPrestadorCadastro: 'portal.prestador.cadastro',
  portalPrestadorContratos: 'portal.prestador.contratos',

  adminHome: 'admin.home',
  adminPerfis: 'admin.perfis',
} as const;

export type FeatureKey = (typeof FEATURES)[keyof typeof FEATURES];

/** Chave usada no conjunto retornado pela API: `feature:action`. */
export function permissionKey(feature: FeatureKey, action: PermissionAction): string {
  return `${feature}:${action}`;
}
